import React, { Component, Fragment } from 'react';
import { Helmet } from 'react-helmet';
import { Link, Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { CSSTransition } from 'react-transition-group';
import * as usersActions from '../../redux/actions/usersActions';
import { userIsAdmin } from '../../utils/permissionsOptions';

import SoftAlert from '../common/SoftAlert';
import UsersListItem from '../features/account/UsersListItem';
import UpDownArrowIcon from '../icons/UpDownArrowIcon';

class UsersPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      sortKey: 'name', // [name, department, permissions]
      sortOrder: 'asc', // [asc, desc]
      permissionsEdited: false,
      serverErrors: [],
      showSoftAlert: false,
    };
  }

  componentDidMount() {
    this.props.usersActions.getUsers();
  }

  toggleSort = (sortKey) => {
    // If we're already sorting by this key, toggle the sort order
    if (this.state.sortKey === sortKey) {
      let newSort = this.state.sortOrder === 'asc' ? 'desc' : 'asc';
      this.sort(sortKey, newSort);
    } else {
      this.sort(sortKey, 'asc');
    }
  }

  sort = (sortKey, sortOrder) => {
    this.setState({ sortKey, sortOrder });
  }

  handleSelectChange = (id, { value }) => {
    // TO DO: add loading state?
    this.props.usersActions.updateUser({
      roleId: parseInt(value, 10),
      userId: id,
    }, this.onSuccess, this.onFail);
  }

  onSuccess = () => {
    // TO DO: remove loading state
  }

  onFail = (error) => {
    // TO DO: remove loading state
    // Override any previous errors' timeouts
    clearTimeout(this.hideSoftAlertTimeout);
    clearTimeout(this.clearSoftAlertDataTimeout);
    this.setState({
      serverErrors: error.response
        ? error.response.data
        : ['There was a problem connecting to the server.'],
      showSoftAlert: true,
    });
    this.hideSoftAlertTimeout = setTimeout(() => {
      // Begin hiding the alert after 2 seconds
      this.setState({ showSoftAlert: false });
      this.clearSoftAlertDataTimeout = setTimeout(() => {
        // Clear the data after the transition is done
        this.setState({ serverErrors: [] });
      }, 200);
    }, 2000);
  }

  sortByName = (a, b) => {
    if (a.lastName < b.lastName) {
      return -1;
    } else if (a.lastName > b.lastName) {
      return 1;
    } else { // compare by first name
      if (a.firstName < b.firstName) return -1;
      if (a.firstName > b.firstName) return 1;
    }
    // the names must be identical
    return 0;
  }

  render() {
    const { sortKey, sortOrder } = this.state;

    if (!userIsAdmin(this.props.user)) {
      // Only allow admins into this page
      return <Redirect to="/account" />;
    }
    let users = Object.values(this.props.users)
      .sort((a, b) => {
        if (sortKey === 'permissions') {
          // Rank in order
          return a.roleId - b.roleId;
        } else if (this.state.sortKey === 'department') {
          if (a.department < b.department) return -1;
          if (a.department > b.department) return 1;
          else return this.sortByName(a, b); // alphabetical fallback
        } else { // we must be sorting by name
          return this.sortByName(a, b);
        }
      })
    if (sortOrder === 'desc') {
      users = users.reverse();
    }

    return (
      <Fragment>
        <Helmet>
          <title>Manage Users</title>
        </Helmet>
        <div className="page-header">
          <div className="row">
            <div className="page-header__button-wrapper">
              <Link to="/account" className="button page-header__button">
                Back to Account
              </Link>
            </div>
            <h1 className="page-header__heading">Manage Users</h1>
            <div className="text page-header__text page-header__text--small">
              <p>
                The online tool has four tiers of permissions for access:
              </p>
              <ul>
                <li>Administrator accounts can change permissions for other users and receive email notifications about activity in the tool.</li>
                <li>Editors are allowed to create and update assessments.</li>
                <li>Viewers can view assessments but cannot edit or upload assessments.</li>
                <li>Deactivated users are no longer allowed access to the tool.</li>
              </ul>
              <p>
                Note: Once a user has been made an Administrator, they cannot be demoted to a lower permissions level.
              </p>
            </div>
          </div>
        </div>
        <div className="user-list">
          <div className="row">
            <div className="user-list__head">
              <div className="user-list__col-1">
                <button
                  type="button"
                  onClick={() => this.toggleSort('name')}
                >
                  User
                  {sortKey === 'name' && (
                    <UpDownArrowIcon direction={this.state.sortOrder} />
                  )}
                </button>
              </div>
              <div className="user-list__col-2">
                <button
                  type="button"
                  onClick={() => this.toggleSort('department')}
                >
                  Department
                  {sortKey === 'department' && (
                    <UpDownArrowIcon direction={this.state.sortOrder} />
                  )}
                </button>
              </div>
              <div className="user-list__col-3">
                <button
                  type="button"
                  onClick={() => this.toggleSort('permissions')}
                >
                  Permissions
                  {sortKey === 'permissions' && (
                    <UpDownArrowIcon direction={this.state.sortOrder} />
                  )}
                </button>
              </div>
            </div>
            {users.map(user =>
              <UsersListItem
                key={user.id}
                user={user}
                handleSelectChange={this.handleSelectChange}
              />
            )}
          </div>
        </div>
        <CSSTransition
          classNames="transition"
          timeout={200}
          in={this.state.showSoftAlert}
          mountOnEnter
          unmountOnExit
        >
          <SoftAlert errors={this.state.serverErrors} />
        </CSSTransition>
      </Fragment>
    );
  }
}

function mapStateToProps(state) {
  return {
    users: state.users.roster,
    user: state.users.roster[state.users.currentUserId] || {},
  };
}

function mapDispatchToProps(dispatch) {
  return { usersActions: bindActionCreators(usersActions, dispatch) };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(UsersPage);



// WEBPACK FOOTER //
// ./src/components/pages/UsersPage.js